<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6">
        {{
          removing
            ? $t("acceptance.pra.file_change.remove_title")
            : $t("acceptance.pra.file_change.title")
        }}
      </v-card-title>
      <v-card-subtitle class="pra-wrap">
        {{ $t("acceptance.pra.file_change.current", { name: currentName }) }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <template v-if="!removing">
          <div class="text-body-2 mb-1">
            {{ $t("acceptance.pra.file_change.new_file") }} *
          </div>
          <div class="pra-file-box d-flex flex-wrap align-center ga-2 pa-3">
            <v-chip v-if="file" size="small" variant="tonal" color="primary">
              {{ file.name }} · {{ fileSize }}
            </v-chip>
            <v-btn
              size="small"
              variant="outlined"
              color="primary"
              :disabled="saving"
              @click="$refs.fileInput.click()"
            >
              {{
                file
                  ? $t("acceptance.pra.crd.dialog.change_file")
                  : $t("acceptance.pra.crd.dialog.choose_file")
              }}
            </v-btn>
            <input
              ref="fileInput"
              type="file"
              class="d-none"
              :accept="accept"
              @change="onFile"
            />
          </div>
          <div
            class="text-caption mt-1 mb-4"
            :class="fileProblem ? 'text-error' : 'text-grey-darken-1'"
          >
            {{ fileProblem || $t("acceptance.pra.crd.dialog.file_hint") }}
          </div>
        </template>

        <v-text-field
          v-model="reason"
          variant="outlined"
          density="comfortable"
          maxlength="500"
          :label="
            reasonRequired
              ? `${$t('acceptance.pra.file_change.reason')} *`
              : $t('acceptance.pra.file_change.reason_optional')
          "
          :hint="
            removing
              ? $t('acceptance.pra.file_change.remove_hint')
              : $t('acceptance.pra.file_change.hint')
          "
          persistent-hint
          :disabled="saving"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("acceptance.pra.crd.dialog.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="submit"
        >
          {{
            removing
              ? $t("acceptance.pra.file_change.remove_save")
              : $t("acceptance.pra.file_change.save")
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { FILE_TYPES } from "@/components/Acceptance/dossierFiles.mjs";
import { formatFileSize } from "@/components/Acceptance/praCrdForm.mjs";
import {
  removeFormErrors,
  replaceFormErrors,
} from "@/components/Acceptance/praFiles.mjs";

// "Sostituisci" on a PRA document: a new file and, when required, a reason. "Rimuovi" (an optional
// document, mode "remove"): only the reason. The old file's bytes are deleted once the change is
// saved; History keeps its name and the reason.
export default {
  name: "AcceptancePraFileDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    // Save failure from the page, shown inside the dialog; the input is kept.
    error: { type: String, default: "" },
    // The file being replaced: { id, originalName }
    current: { type: Object, default: null },
    // A Manager / Admin changing someone else's file, or any change once the case is closed
    reasonRequired: { type: Boolean, default: false },
    // "replace" (a new file) or "remove" (an optional document, no new file)
    mode: { type: String, default: "replace" },
  },
  emits: ["update:modelValue", "submit"],
  data: () => ({
    file: null,
    reason: "",
  }),
  computed: {
    accept() {
      return FILE_TYPES.join(",");
    },
    removing() {
      return this.mode === "remove";
    },
    currentName() {
      return (this.current && this.current.originalName) || "—";
    },
    errors() {
      if (this.removing) {
        return removeFormErrors(
          { reason: this.reason },
          { reasonRequired: this.reasonRequired },
        );
      }
      return replaceFormErrors(
        { file: this.file, reason: this.reason },
        { reasonRequired: this.reasonRequired },
      );
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
    fileSize() {
      return this.file ? formatFileSize(this.file.size) : "";
    },
    fileProblem() {
      const reason = this.errors.file;
      if (!reason || reason === "required") return "";
      return this.$t(`acceptance.files.errors.${reason}`);
    },
  },
  watch: {
    modelValue(open) {
      if (!open) return;
      this.file = null;
      this.reason = "";
    },
  },
  methods: {
    onFile(event) {
      const [file] = event.target.files || [];
      if (file) this.file = file;
      // The same file can be picked again after a change.
      event.target.value = ""; // eslint-disable-line no-param-reassign
    },
    submit() {
      if (!this.valid || this.saving) return;
      this.$emit("submit", {
        file: this.removing ? null : this.file,
        reason: this.reason.trim(),
      });
    },
  },
};
</script>

<style scoped>
.pra-wrap {
  white-space: normal;
}
.pra-file-box {
  border: 1px dashed rgba(0, 0, 0, 0.24);
  border-radius: 4px;
}
</style>
